import React, { useContext, useEffect, useState } from "react";
import { landingPageContext } from "../LandingEdits";

interface Achiever {
  id: string;
  image: string;
  name: string;
  homeDistrict: string;
  batch: string;
  selectedExam: string;
  selectedPost: string;
  feedback: string;
  podcastLink: string;
}

const AchieversEditor: React.FC = () => {
  const context = useContext(landingPageContext);
  if (!context) throw new Error("Must be used inside LandingEdits");

  const { landingDetails, setLandingDetails } = context;

  const [achievers, setAchievers] = useState<Achiever[]>([]);

  useEffect(() => {
    setAchievers(landingDetails.achievers || []);
  }, [landingDetails.achievers]);

  const updateContext = (updated: Achiever[]) => {
    setAchievers(updated);
    setLandingDetails((prev: any) => ({ ...prev, achievers: updated }));
  };

  const handleInputChange = (id: string, field: keyof Achiever, value: string) => {
    const updated = achievers.map((a) => (a.id === id ? { ...a, [field]: value } : a));
    updateContext(updated);
  };

  const handleImageUpload = (id: string, file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      handleInputChange(id, "image", base64);
    };
    reader.readAsDataURL(file);
  };

  const handleAddAchiever = () => {
    const newAchiever: Achiever = {
      id: Date.now().toString(),
      image: "",
      name: "",
      homeDistrict: "",
      batch: "",
      selectedExam: "",
      selectedPost: "",
      feedback: "",
      podcastLink: "",
    };

    updateContext([...achievers, newAchiever]);
  };

  const handleDeleteAchiever = (id: string) => {
    updateContext(achievers.filter((a) => a.id !== id));
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold">Achievers Section Editor</h2>

      <button onClick={handleAddAchiever} className="bg-green-600 text-white px-4 py-2 rounded">
        + Add Achiever
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {achievers.map((achiever) => (
          <div key={achiever.id} className="border rounded-xl p-4 space-y-3 shadow">
            {achiever.image && (
              <img src={achiever.image} alt={achiever.name} className="w-full h-40 object-cover rounded" />
            )}

            <label className="block text-sm font-medium">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(achiever.id, file);
              }}
              className="w-full text-sm"
            />

            <label className="block text-sm font-medium">Name</label>
            <input
              type="text"
              value={achiever.name}
              onChange={(e) => handleInputChange(achiever.id, "name", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Home District</label>
            <input
              type="text"
              value={achiever.homeDistrict}
              onChange={(e) => handleInputChange(achiever.id, "homeDistrict", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Course</label>
            <input
              type="text"
              value={achiever.batch}
              onChange={(e) => handleInputChange(achiever.id, "batch", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Selected in</label>
            <input
              type="text"
              value={achiever.selectedExam}
              onChange={(e) => handleInputChange(achiever.id, "selectedExam", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Service/Post</label>
            <input
              type="text"
              value={achiever.selectedPost}
              onChange={(e) => handleInputChange(achiever.id, "selectedPost", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Testimonial</label>
            <textarea
              value={achiever.feedback}
              onChange={(e) => handleInputChange(achiever.id, "feedback", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Podcast Link</label>
            <input
              type="text"
              value={achiever.podcastLink}
              onChange={(e) => handleInputChange(achiever.id, "podcastLink", e.target.value)}
              className="w-full border p-2 rounded"
            />

            <button
              onClick={() => handleDeleteAchiever(achiever.id)}
              className="bg-red-500 text-white px-4 py-2 rounded w-full"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AchieversEditor;
